// Libs
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useParams } from 'react-router';
import { toast } from 'sonner';

// RDS
import Heading from '@ramsey-design-system/heading';

// Components
import Divider from '~fpu/shared/components/Divider';
import WorkshopStepFactory from './CreateWorkshop/WorkshopStepFactory';
import {
  STEPS,
  useWorkshopStepContext,
  WorkshopStepProvider
} from './CreateWorkshop/WorkshopStepContext';
import CancelWorkshop from './CreateWorkshop/CancelWorkshop';

// Hooks
import { useUpdateWorkshop } from './hooks/useUpdateWorkshop';
import { useWorkshopDetails } from './hooks/useWorkshopDetails';

// Utils
import { PATHS } from '~fpu/config/constants';
import { prepareWorkshopUpdatePayload } from './CreateWorkshop/utils/prepare-workshop-payload';

// Types
import { WorkshopFormValues } from './CreateWorkshop/types';

function EditWorkshop() {
  const { workshopId } = useParams() as { workshopId: string };

  const { data: workshop, isLoading } = useWorkshopDetails(workshopId);

  if (isLoading || !workshop) return null;

  if (workshop.status === 'CANCELED') {
    return (
      <Navigate
        to={PATHS.FPU_DASHBOARD.BUDGET_WORKSHOP_DETAIL(workshopId)}
        replace
      />
    );
  }

  return (
    <div className="mx-auto max-w-[42.75rem]">
      <Heading
        level="2"
        className="text-8 text-blue-80 sm:text-10 pb-2 sm:pb-3.5"
      >
        Edit Workshop
      </Heading>
      <Divider className="mb-3.5" />
      <WorkshopStepProvider isEditMode>
        <EditWorkshopForm workshopId={workshopId} />
        <CancelWorkshop />
      </WorkshopStepProvider>
    </div>
  );
}
function EditWorkshopForm({ workshopId }: { workshopId: string }) {
  const { data: workshop, isLoading } = useWorkshopDetails(workshopId);

  const { helpers } = useWorkshopStepContext();

  const methods = useForm<WorkshopFormValues>({ mode: 'onChange' });
  const { handleSubmit } = methods;

  const { mutate, isPending } = useUpdateWorkshop(workshopId);

  useEffect(() => {
    if (workshop) {
      methods.reset({
        name: workshop.name,
        location: workshop.location || '',
        workshopDate: workshop.workshopDate,
        startTime: workshop.startTime,
        additionalNotes: workshop.additionalNotes || ''
      });
    }
  }, [workshop, methods]);

  if (isLoading || !workshop) return null;

  const onSubmit = (data: WorkshopFormValues) => {
    const payload = prepareWorkshopUpdatePayload(data);

    mutate(payload, {
      onSuccess: () => {
        helpers.setStep(STEPS.SUMMARY);
        toast.success('Workshop was successfully updated.', {
          duration: 6000
        });
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="rounded-xl bg-white px-2 py-4 shadow-md sm:px-4 sm:py-6"
      >
        <WorkshopStepFactory isSubmitting={isPending} />
      </form>
    </FormProvider>
  );
}

export default EditWorkshop;
